import { money, fileUrl, formatDate } from "@/lib/api";
import { StatusBadge, EmptyState } from "@/components/shared";
import { ScrollText, FileText, PenLine } from "lucide-react";

export const RenterLeasesTab = ({ leases, onSignDoc, onSignLease }) => {
  if (leases.length === 0) {
    return <div className="bg-white border border-line rounded-xl"><EmptyState icon={ScrollText} title="No lease yet" subtitle="Once a landlord approves your application and sends a lease, it will appear here to review and e-sign." /></div>;
  }
  return (
    <div className="space-y-4">
      {leases.map((l) => (
        <div key={l.id} data-testid={`lease-${l.id}`} className="bg-white border border-line rounded-xl p-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2"><h3 className="font-heading font-medium text-ink text-lg">{l.property_title}</h3><StatusBadge status={l.status} /></div>
            <p className="text-sm text-mute">{money(l.monthly_rent)}/mo · Deposit {money(l.security_deposit)}</p>
          </div>
          <p className="text-sm text-mute mt-1">Landlord {l.landlord_name} · {l.start_date} → {l.end_date}</p>
          <div className="mt-4 bg-bone rounded-lg p-4 text-sm text-ink leading-relaxed whitespace-pre-wrap max-h-40 overflow-y-auto">{l.terms}</div>
          {l.document && <a data-testid={`renter-doc-${l.id}`} href={l.document} download={l.document_name || "lease"} className="mt-2 inline-flex items-center gap-1 text-sm text-forest hover:underline"><FileText className="h-3.5 w-3.5" /> {l.document_name || "Lease document"}</a>}

          {(l.documents || []).length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium text-ink">Documents to sign</p>
              {l.documents.map((d) => (
                <div key={d.id} data-testid={`lease-doc-row-${d.id}`} className="flex items-center justify-between gap-3 border border-line rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-mute shrink-0" />
                    <span className="text-sm text-ink truncate">{d.name}</span>
                  </div>
                  {d.signed ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-emerald-700 font-medium">✓ Signed</span>
                      {d.signed_path ? (
                        <a data-testid={`download-signed-${d.id}`} href={fileUrl(d.signed_path)} target="_blank" rel="noreferrer" className="text-xs text-forest hover:underline">Download</a>
                      ) : (
                        <span className="text-xs text-mute">archived</span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 shrink-0">
                      <a href={fileUrl(d.path)} target="_blank" rel="noreferrer" className="text-xs text-mute hover:text-forest">View</a>
                      <button data-testid={`sign-doc-${d.id}`} onClick={() => onSignDoc(l, d)} className="inline-flex items-center gap-1 bg-forest text-white px-2.5 py-1 rounded-lg text-xs hover:bg-forest-hover"><PenLine className="h-3 w-3" /> Sign</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {(l.info_docs || []).length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-mute mb-1">Informational documents</p>
              <div className="flex flex-wrap gap-2">
                {l.info_docs.map((d, i) => (
                  <a key={i} data-testid={`info-doc-${l.id}-${i}`} href={fileUrl(d.path)} target="_blank" rel="noreferrer" className="text-xs border border-line rounded-full px-2.5 py-1 text-ink hover:bg-bone inline-flex items-center gap-1"><FileText className="h-3 w-3" /> {d.name}</a>
                ))}
              </div>
            </div>
          )}

          {l.status === "pending_signature" && !(l.documents || []).length && (
            <button data-testid={`sign-lease-${l.id}`} onClick={() => onSignLease(l)} className="mt-4 bg-forest text-white px-5 py-2.5 rounded-lg font-medium hover:bg-forest-hover flex items-center gap-2"><PenLine className="h-4 w-4" /> Review & E-Sign</button>
          )}
          {l.status === "signed" && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <p className="text-sm text-emerald-700 font-medium">✓ All documents signed{l.signed_at ? ` on ${formatDate(l.signed_at)}` : ""}</p>
              {l.signed_pdf && <a data-testid={`renter-signed-pdf-${l.id}`} href={l.signed_pdf} download={l.signed_pdf_name || "signed-lease.pdf"} className="inline-flex items-center gap-1.5 bg-forest text-white px-3 py-1.5 rounded-lg text-sm hover:bg-forest-hover transition-colors"><ScrollText className="h-3.5 w-3.5" /> Download Signed PDF</a>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
